import { Box } from '@mui/material';
import { theme } from '@repo/ui/NerdWalletThemeProvider';
import { ReactNode, useEffect, useRef } from 'react';
import { MobileDialogFooter } from './MobileDialogFooter';
import { MobileDialogHeader } from './MobileDialogHeader';
import { MobileDialogStoreProps } from './types';
import { useScrollLockOnMobile } from './useScrollLockOnMobile';

type MobileDialogProps<TFilterState, TSortValue> = {
  isDialogOpen: boolean;
  handleOpenCloseDialog: () => void;
  dialogId: string;
  title?: string;
  children: ReactNode;
  useStore: MobileDialogStoreProps<TFilterState, TSortValue>;
  filterPillsToDisplay: number;
  actionSection: string;
  pageIdentifier: string;
  onLocationReset?: () => void;
};

export const MobileDialog = <TFilterState, TSortValue>({
  isDialogOpen,
  handleOpenCloseDialog,
  dialogId,
  children,
  useStore,
  filterPillsToDisplay,
  actionSection,
  pageIdentifier,
  onLocationReset,
}: MobileDialogProps<TFilterState, TSortValue>) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useScrollLockOnMobile(isDialogOpen);

  useEffect(() => {
    if (isDialogOpen) {
      dialogRef.current?.focus();
    }
  }, [isDialogOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleOpenCloseDialog();
    };
    if (isDialogOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDialogOpen, handleOpenCloseDialog]);

  return (
    <Box
      component="dialog"
      aria-labelledby={dialogId}
      open={isDialogOpen}
      ref={dialogRef}
      aria-modal="true"
      sx={{
        zIndex: theme.zIndex.modal,
      }}
    >
      <Box
        sx={{
          inset: 0,
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <MobileDialogHeader
          handleOpenCloseDialog={handleOpenCloseDialog}
          useStore={useStore}
          dialogId={dialogId}
          filterPillsToDisplay={filterPillsToDisplay}
          actionSection={actionSection}
          pageIdentifier={pageIdentifier}
          onLocationReset={onLocationReset}
        />
        {children}
        <MobileDialogFooter handleOpenCloseDialog={handleOpenCloseDialog} />
      </Box>
    </Box>
  );
};
